import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Smartphone,
    CheckCircle,
    Shield,
    QrCode,
    RefreshCw,
    Trash2,
    StopCircle,
    PlayCircle,
    Lock,
    CheckCircle2,
    Clock,
    Ban,
    Copy,
    Check,
    Search,
    Layers
} from 'lucide-react';
import DataTable from './common/DataTable';
import InfoModal from './common/InfoModal';
import { apiClient } from '../utils/apiClient';
import mobileDeviceApi from '../services/mobileDeviceApi';
import { useAuth } from '../contexts/AuthContext';

export default function MobileDeviceSettings() {
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [users, setUsers] = useState([]);
    const [isPairingModalOpen, setIsPairingModalOpen] = useState(false);
    const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
    const [employeeSearch, setEmployeeSearch] = useState('');
    const [generatedCode, setGeneratedCode] = useState(null);
    const [pairingLoading, setPairingLoading] = useState(false);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [copiedCode, setCopiedCode] = useState(false);

    // Policy State
    const [autoApproveFirst, setAutoApproveFirst] = useState(false);
    const [allowMultipleDevices, setAllowMultipleDevices] = useState(false);
    const [policyLoading, setPolicyLoading] = useState(false);

    // Modal State
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [confirmActionData, setConfirmActionData] = useState(null);

    const { permissions } = useAuth();
    const canView = permissions.includes('mobile_devices:view_list') || permissions.includes('settings:manage');
    const canManage =
        permissions.includes('mobile_devices:approve') ||
        permissions.includes('mobile_devices:manage_policy') ||
        permissions.includes('settings:manage');
    const canGenerateCode =
        permissions.includes('mobile_devices:generate_pairing_code') ||
        permissions.includes('mobile_devices:manage_policy') ||
        permissions.includes('settings:manage');
    const canManageSettings =
        permissions.includes('mobile_devices:manage_policy') ||
        permissions.includes('settings:manage');

    const fetchDevices = useCallback(async () => {
        if (!canView) return;
        setLoading(true);
        try {
            const data = await mobileDeviceApi.list();
            setDevices(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error(err);
            setError('Mobil cihaz listesi alınamadı.');
        } finally {
            setLoading(false);
        }
    }, [canView]);

    const fetchSettings = useCallback(async () => {
        try {
            const settings = await mobileDeviceApi.getSettings();
            const autoApprove = settings.find(s => s.key === "Mobile.AutoApproveFirstDevice");
            if (autoApprove) {
                setAutoApproveFirst(autoApprove.value === "true");
            }
            const allowMulti = settings.find(s => s.key === "Mobile.AllowMultipleDevices");
            if (allowMulti) {
                setAllowMultipleDevices(allowMulti.value === "true");
            }
        } catch (err) {
            console.error("Mobil cihaz politikası ayarları alınamadı", err);
        }
    }, []);

    const fetchEmployeesAndUsers = useCallback(async () => {
        if (employees.length > 0) return;
        try {
            const [empData, userData] = await Promise.all([
                apiClient('Employee'),
                apiClient('User').catch(() => [])
            ]);
            setEmployees(Array.isArray(empData) ? empData : []);
            setUsers(Array.isArray(userData) ? userData : []);
        } catch (err) {
            console.error("Personel/Kullanıcı listesi alınamadı", err);
        }
    }, [employees.length]);

    useEffect(() => {
        fetchDevices();
        fetchSettings();
    }, [fetchDevices, fetchSettings]);

    const handleSetAutoApprovePolicy = async (targetValue) => {
        if (policyLoading || !canManageSettings || targetValue === autoApproveFirst) return;
        setPolicyLoading(true);
        setError(null);
        setMessage(null);
        try {
            await mobileDeviceApi.updateSetting("Mobile.AutoApproveFirstDevice", targetValue ? "true" : "false");
            setAutoApproveFirst(targetValue);
            setMessage(
                targetValue
                    ? 'Yeni Cihaz Onay Politikası güncellendi: İlk kez giriş yapan personelin ilk cihazı otomatik onaylanacak.'
                    : 'Yeni Cihaz Onay Politikası güncellendi: Her yeni cihaz için yönetici onayı veya eşleştirme kodu gerekecek.'
            );
        } catch (err) {
            setError(err?.message || "Yeni cihaz onay politikası güncellenemedi.");
        } finally {
            setPolicyLoading(false);
        }
    };

    const handleSetMultiDevicePolicy = async (targetValue) => {
        if (policyLoading || !canManageSettings || targetValue === allowMultipleDevices) return;
        setPolicyLoading(true);
        setError(null);
        setMessage(null);
        try {
            const response = await mobileDeviceApi.updateSetting("Mobile.AllowMultipleDevices", targetValue ? "true" : "false");
            setAllowMultipleDevices(targetValue);
            const deactivatedCount = response?.deactivatedDeviceCount || 0;
            if (!targetValue && deactivatedCount > 0) {
                setMessage(`Tek Cihaz Politikası aktif edildi. Birden fazla onaylı cihazı bulunan personellerin ${deactivatedCount} adet eski cihazı otomatik olarak pasife alındı.`);
                fetchDevices();
            } else {
                setMessage(
                    targetValue
                        ? 'Çoklu Cihaz Politikası güncellendi: Personeller birden fazla onaylı mobil cihazla giriş yapabilir.'
                        : 'Tek Cihaz Politikası güncellendi: Her personel yalnızca tek bir aktif/onaylı mobil cihaz kullanabilir.'
                );
                if (!targetValue) fetchDevices();
            }
        } catch (err) {
            setError(err?.message || "Çoklu cihaz politikası güncellenemedi.");
        } finally {
            setPolicyLoading(false);
        }
    };

    const handleActionRequest = (device, action) => {
        setConfirmActionData({
            userId: device.userId,
            deviceId: device.deviceId,
            userName: device.fullName || device.userName,
            model: `${device.platform || ''} ${device.model || ''}`.trim(),
            action
        });
        setIsConfirmModalOpen(true);
    };

    const handleActionConfirm = async () => {
        if (!confirmActionData) return;
        const { userId, deviceId, action } = confirmActionData;
        setIsConfirmModalOpen(false);
        setLoading(true);
        setError(null);
        setMessage(null);
        try {
            await mobileDeviceApi.adminAction(userId, deviceId, action);
            const actionLabel = action === 'Approve' ? 'onaylandı' : action === 'Block' ? 'engellendi' : 'silindi';
            setMessage(
                action === 'Approve' && !allowMultipleDevices
                    ? `Cihaz onaylandı. Tek cihaz politikası gereği personelin varsa diğer cihazları otomatik olarak pasife alındı.`
                    : `Cihaz başarıyla ${actionLabel}.`
            );
            fetchDevices();
        } catch (err) {
            setError(err.message || 'Cihaz işlemi başarısız oldu.');
            setLoading(false);
        }
    };

    const handleGenerateCode = async () => {
        if (!selectedEmployeeId) {
            setError('Lütfen bir personel seçin.');
            return;
        }

        const empIdNum = parseInt(selectedEmployeeId, 10);
        const selectedEmp = employees.find(e => e.id === empIdNum);

        // Kullanıcıyı employeeId, T.C. Kimlik No veya E-posta üzerinden eşleştir, bulunamazsa backend employeeId üzerinden çözer
        const targetUser = users.find(u =>
            u.employeeId === empIdNum ||
            (selectedEmp?.turkishIdNo && u.username === selectedEmp.turkishIdNo) ||
            (selectedEmp?.email && u.email && u.email.toLowerCase() === selectedEmp.email.toLowerCase())
        );

        setPairingLoading(true);
        setGeneratedCode(null);
        setError(null);
        try {
            const response = await mobileDeviceApi.generatePairingCode({
                userId: targetUser?.id || 0,
                employeeId: empIdNum
            });
            setGeneratedCode(response);
        } catch (err) {
            setError(err.message || 'Eşleştirme kodu üretilemedi.');
        } finally {
            setPairingLoading(false);
        }
    };

    const openPairingModal = () => {
        setIsPairingModalOpen(true);
        setGeneratedCode(null);
        setSelectedEmployeeId('');
        setEmployeeSearch('');
        setCopiedCode(false);
        setError(null);
        fetchEmployeesAndUsers();
    };

    const copyPairingCode = () => {
        if (!generatedCode?.code) return;
        navigator.clipboard.writeText(generatedCode.code);
        setCopiedCode(true);
        setTimeout(() => setCopiedCode(false), 2000);
    };

    // İstatistikler
    const stats = useMemo(() => {
        const total = devices.length;
        const blocked = devices.filter(d => d.isBlocked).length;
        const approved = devices.filter(d => d.isApproved && !d.isBlocked).length;
        const pending = devices.filter(d => !d.isApproved && !d.isBlocked).length;
        return { total, approved, pending, blocked };
    }, [devices]);

    // Filtrelenmiş cihazlar
    const filteredDevices = useMemo(() => {
        if (statusFilter === 'APPROVED') return devices.filter(d => d.isApproved && !d.isBlocked);
        if (statusFilter === 'PENDING') return devices.filter(d => !d.isApproved && !d.isBlocked);
        if (statusFilter === 'BLOCKED') return devices.filter(d => d.isBlocked);
        return devices;
    }, [devices, statusFilter]);

    // Modal içi personel filtreleme
    const filteredEmployees = useMemo(() => {
        const q = employeeSearch.trim().toLowerCase();
        if (!q) return employees;
        return employees.filter(emp =>
            `${emp.firstName || ''} ${emp.lastName || ''}`.toLowerCase().includes(q) ||
            (emp.turkishIdNo || '').toLowerCase().includes(q) ||
            (emp.sicilNo || emp.employeeNumber || '').toLowerCase().includes(q)
        );
    }, [employees, employeeSearch]);

    const columns = useMemo(() => [
        {
            header: 'Personel / Kullanıcı',
            accessor: 'userName',
            sortable: true,
            searchable: true,
            render: r => (
                <div className="flex flex-col">
                    <span className="font-semibold text-gray-900 dark:text-white">
                        {r.fullName || r.userName || '-'}
                    </span>
                    <div className="flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                        {r.employeeNumber && (
                            <span className="font-mono bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">
                                #{r.employeeNumber}
                            </span>
                        )}
                        {r.loginUsername && r.loginUsername !== (r.fullName || r.userName) && (
                            <span className="font-mono text-gray-400 dark:text-gray-500">
                                ({r.loginUsername})
                            </span>
                        )}
                    </div>
                </div>
            )
        },
        {
            header: 'Bölüm',
            accessor: 'departmentName',
            sortable: true,
            searchable: true,
            render: r => (
                <span className="text-xs text-gray-600 dark:text-gray-300">
                    {r.departmentName || '-'}
                </span>
            )
        },
        {
            header: 'Platform / Model',
            accessor: 'model',
            sortable: true,
            searchable: true,
            render: r => (
                <div className="flex flex-col">
                    <div className="flex items-center gap-1.5">
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 border border-indigo-200/60 dark:border-indigo-500/20">
                            {r.platform || 'Mobil'}
                        </span>
                        <span className="text-xs font-medium text-gray-800 dark:text-gray-200">
                            {r.model || '-'}
                        </span>
                    </div>
                    <span
                        className="font-mono text-[11px] text-gray-400 dark:text-gray-500 mt-0.5"
                        title={r.deviceId}
                    >
                        ID: {r.deviceId ? `${r.deviceId.substring(0, 14)}...` : '-'}
                    </span>
                </div>
            )
        },
        {
            header: 'Sürüm',
            accessor: 'appVersion',
            render: r => (
                <span className="font-mono text-xs text-gray-600 dark:text-gray-400">
                    {r.appVersion || '-'}
                </span>
            )
        },
        {
            header: 'Durum',
            accessor: 'status',
            render: r => {
                if (r.isBlocked) {
                    return (
                        <span className="inline-flex items-center gap-1 bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-500/30 px-2.5 py-1 rounded-full text-xs font-semibold">
                            <Ban className="w-3.5 h-3.5" /> Engelli
                        </span>
                    );
                }
                if (!r.isApproved) {
                    return (
                        <span className="inline-flex items-center gap-1 bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-500/30 px-2.5 py-1 rounded-full text-xs font-semibold">
                            <Clock className="w-3.5 h-3.5" /> Onay Bekliyor
                        </span>
                    );
                }
                return (
                    <span className="inline-flex items-center gap-1 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-500/30 px-2.5 py-1 rounded-full text-xs font-semibold">
                        <CheckCircle className="w-3.5 h-3.5" /> Onaylı
                    </span>
                );
            }
        },
        {
            header: 'Son Görülme',
            accessor: 'lastSeenAt',
            sortable: true,
            render: r => (
                <span className="text-xs text-gray-600 dark:text-gray-300">
                    {r.lastSeenAt ? new Date(r.lastSeenAt).toLocaleString('tr-TR') : '-'}
                </span>
            )
        },
        {
            header: 'İşlemler',
            accessor: 'actions',
            render: r => (
                <div className="flex items-center gap-1.5">
                    {canManage && !r.isApproved && !r.isBlocked && (
                        <button
                            type="button"
                            onClick={() => handleActionRequest(r, 'Approve')}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-500/20 border border-emerald-200/70 dark:border-emerald-500/30 transition-colors"
                            title="Cihazı Onayla"
                        >
                            <CheckCircle className="w-3.5 h-3.5" />
                            Onayla
                        </button>
                    )}
                    {canManage && !r.isBlocked && (
                        <button
                            type="button"
                            onClick={() => handleActionRequest(r, 'Block')}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-500/20 border border-amber-200/70 dark:border-amber-500/30 transition-colors"
                            title="Cihazı Engelle"
                        >
                            <StopCircle className="w-3.5 h-3.5" />
                            Engelle
                        </button>
                    )}
                    {canManage && r.isBlocked && (
                        <button
                            type="button"
                            onClick={() => handleActionRequest(r, 'Approve')}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-500/20 border border-indigo-200/70 dark:border-indigo-500/30 transition-colors"
                            title="Engeli Kaldır ve Onayla"
                        >
                            <PlayCircle className="w-3.5 h-3.5" />
                            Engeli Kaldır
                        </button>
                    )}
                    {canManage && (
                        <button
                            type="button"
                            onClick={() => handleActionRequest(r, 'Delete')}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-colors"
                            title="Cihaz Kaydını Sil ve Oturumu Sonlandır"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    )}
                </div>
            )
        }
    ], [canManage]);

    if (!canView) {
        return <div className="p-4 text-rose-500">Mobil cihazları görüntüleme yetkiniz yok.</div>;
    }

    return (
        <div className="space-y-4">
            {/* Header & Policies Section */}
            <div className="bg-white dark:bg-[#18191c] p-5 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-5">
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-200/70 dark:border-indigo-500/20 flex items-center justify-center text-indigo-600 dark:text-indigo-400 shrink-0">
                            <Smartphone className="w-5 h-5" />
                        </div>
                        <div>
                            <h3 className="text-base font-bold text-gray-900 dark:text-white">
                                Mobil Cihaz Yönetimi & Güvenlik Politikaları
                            </h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                Mobil uygulama girişlerinde tekil/çoklu cihaz kısıtlamalarını, ilk cihaz onay davranışını ve kayıtlı telefonları yönetin.
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 self-end md:self-auto">
                        <button
                            type="button"
                            onClick={fetchDevices}
                            className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 transition-colors"
                            title="Cihaz Listesini Yenile"
                        >
                            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                        {canGenerateCode && (
                            <button
                                type="button"
                                onClick={openPairingModal}
                                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl shadow-sm text-xs font-semibold transition-colors"
                            >
                                <QrCode className="w-4 h-4" />
                                Eşleştirme Kodu Üret
                            </button>
                        )}
                    </div>
                </div>

                {/* Bildirim Mesajları */}
                {message && (
                    <div className="p-3 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 rounded-xl border border-emerald-200 dark:border-emerald-800/50 text-xs font-medium mb-4 flex items-center justify-between">
                        <span>{message}</span>
                        <button type="button" onClick={() => setMessage(null)} className="text-emerald-500 hover:text-emerald-700 font-bold ml-2">✕</button>
                    </div>
                )}
                {error && (
                    <div className="p-3 bg-rose-50 dark:bg-rose-950/30 text-rose-700 dark:text-rose-300 rounded-xl border border-rose-200 dark:border-rose-800/50 text-xs font-medium mb-4 flex items-center justify-between">
                        <span>{error}</span>
                        <button type="button" onClick={() => setError(null)} className="text-rose-500 hover:text-rose-700 font-bold ml-2">✕</button>
                    </div>
                )}

                {/* Policy Toggle Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                    {/* Yeni Cihaz Onay Politikası */}
                    <div className="flex flex-col justify-between p-4 bg-gray-50/80 dark:bg-[#111214] rounded-xl border border-gray-200/80 dark:border-gray-800">
                        <div className="flex items-start gap-3 mb-4">
                            <div className={`p-2.5 rounded-xl shrink-0 ${
                                autoApproveFirst
                                    ? 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                                    : 'bg-amber-100 dark:bg-amber-500/15 text-amber-600 dark:text-amber-400'
                            }`}>
                                <Shield className="w-5 h-5" />
                            </div>
                            <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <h4 className="text-sm font-bold text-gray-900 dark:text-gray-100">Yeni Cihaz Onay Politikası</h4>
                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                                        autoApproveFirst
                                            ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                                            : 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300'
                                    }`}>
                                        {autoApproveFirst ? 'İlk Cihaz Otomatik' : 'Yönetici Onaylı'}
                                    </span>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                                    {autoApproveFirst
                                        ? "Hiç cihazı olmayan personelin ilk giriş yaptığı telefon otomatik onaylanır. Sonraki cihaz değişimleri için onay veya eşleştirme kodu gerekir."
                                        : "Sıkı Güvenlik: İlk giriş dahil her yeni mobil cihaz için yönetici onayı veya 6 haneli eşleştirme kodu zorunludur."}
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-1.5 bg-gray-200/80 dark:bg-[#18191c] p-1 rounded-xl border border-gray-200 dark:border-gray-800">
                            <button
                                type="button"
                                onClick={() => handleSetAutoApprovePolicy(false)}
                                disabled={policyLoading || !canManageSettings}
                                className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                                    !autoApproveFirst
                                        ? 'bg-amber-500 text-white shadow-sm'
                                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                                } ${!canManageSettings ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                Her Cihaza Kod Sor
                            </button>
                            <button
                                type="button"
                                onClick={() => handleSetAutoApprovePolicy(true)}
                                disabled={policyLoading || !canManageSettings}
                                className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                                    autoApproveFirst
                                        ? 'bg-emerald-600 text-white shadow-sm'
                                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                                } ${!canManageSettings ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                İlk Girişi Onayla
                            </button>
                        </div>
                    </div>

                    {/* Çoklu Cihaz Politikası */}
                    <div className="flex flex-col justify-between p-4 bg-gray-50/80 dark:bg-[#111214] rounded-xl border border-gray-200/80 dark:border-gray-800">
                        <div className="flex items-start gap-3 mb-4">
                            <div className={`p-2.5 rounded-xl shrink-0 ${
                                allowMultipleDevices
                                    ? 'bg-indigo-100 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-400'
                                    : 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                            }`}>
                                {allowMultipleDevices ? <Layers className="w-5 h-5" /> : <Lock className="w-5 h-5" />}
                            </div>
                            <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <h4 className="text-sm font-bold text-gray-900 dark:text-gray-100">Çoklu Cihaz Politikası</h4>
                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                                        allowMultipleDevices
                                            ? 'bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300'
                                            : 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                                    }`}>
                                        {allowMultipleDevices ? 'Çoklu Cihaz Açık' : 'Tek Cihaz Koruması'}
                                    </span>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                                    {allowMultipleDevices
                                        ? "Esnek Kullanım: Personel onaylanmış birden fazla mobil cihazda (ör. kişisel + şirket telefonu) aynı anda oturum açabilir."
                                        : "Tek Cihaz Koruması: Personel yalnızca 1 onaylı cihaz kullanabilir. Yeni bir cihaz onaylandığında eski cihaz otomatik olarak devre dışı bırakılır."}
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-1.5 bg-gray-200/80 dark:bg-[#18191c] p-1 rounded-xl border border-gray-200 dark:border-gray-800">
                            <button
                                type="button"
                                onClick={() => handleSetMultiDevicePolicy(false)}
                                disabled={policyLoading || !canManageSettings}
                                className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                                    !allowMultipleDevices
                                        ? 'bg-emerald-600 text-white shadow-sm'
                                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                                } ${!canManageSettings ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                Sadece Tek Cihaz
                            </button>
                            <button
                                type="button"
                                onClick={() => handleSetMultiDevicePolicy(true)}
                                disabled={policyLoading || !canManageSettings}
                                className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                                    allowMultipleDevices
                                        ? 'bg-indigo-600 text-white shadow-sm'
                                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                                } ${!canManageSettings ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                Çoklu Cihaza İzin Ver
                            </button>
                        </div>
                    </div>
                </div>

                {/* KPI Özet ve Durum Filtre Kartları */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <button
                        type="button"
                        onClick={() => setStatusFilter('ALL')}
                        className={`p-3.5 rounded-xl border text-left transition-all ${
                            statusFilter === 'ALL'
                                ? 'bg-indigo-50/70 dark:bg-indigo-500/10 border-indigo-400 dark:border-indigo-500/50 ring-1 ring-indigo-500/20'
                                : 'bg-gray-50/70 dark:bg-[#111214] border-gray-200/80 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'
                        }`}
                    >
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Toplam Cihaz</span>
                            <Smartphone className="w-4 h-4 text-indigo-500" />
                        </div>
                        <p className="text-xl font-bold text-gray-900 dark:text-white mt-1">{stats.total}</p>
                    </button>

                    <button
                        type="button"
                        onClick={() => setStatusFilter('APPROVED')}
                        className={`p-3.5 rounded-xl border text-left transition-all ${
                            statusFilter === 'APPROVED'
                                ? 'bg-emerald-50/70 dark:bg-emerald-500/10 border-emerald-400 dark:border-emerald-500/50 ring-1 ring-emerald-500/20'
                                : 'bg-gray-50/70 dark:bg-[#111214] border-gray-200/80 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'
                        }`}
                    >
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Onaylı / Aktif</span>
                            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        </div>
                        <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">{stats.approved}</p>
                    </button>

                    <button
                        type="button"
                        onClick={() => setStatusFilter('PENDING')}
                        className={`p-3.5 rounded-xl border text-left transition-all ${
                            statusFilter === 'PENDING'
                                ? 'bg-amber-50/70 dark:bg-amber-500/10 border-amber-400 dark:border-amber-500/50 ring-1 ring-amber-500/20'
                                : 'bg-gray-50/70 dark:bg-[#111214] border-gray-200/80 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'
                        }`}
                    >
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Onay Bekleyen</span>
                            <Clock className="w-4 h-4 text-amber-500" />
                        </div>
                        <p className="text-xl font-bold text-amber-600 dark:text-amber-400 mt-1">{stats.pending}</p>
                    </button>

                    <button
                        type="button"
                        onClick={() => setStatusFilter('BLOCKED')}
                        className={`p-3.5 rounded-xl border text-left transition-all ${
                            statusFilter === 'BLOCKED'
                                ? 'bg-rose-50/70 dark:bg-rose-500/10 border-rose-400 dark:border-rose-500/50 ring-1 ring-rose-500/20'
                                : 'bg-gray-50/70 dark:bg-[#111214] border-gray-200/80 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'
                        }`}
                    >
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Engellenen</span>
                            <Ban className="w-4 h-4 text-rose-500" />
                        </div>
                        <p className="text-xl font-bold text-rose-600 dark:text-rose-400 mt-1">{stats.blocked}</p>
                    </button>
                </div>
            </div>

            {/* Table */}
            <DataTable
                data={filteredDevices}
                columns={columns}
                isLoading={loading}
                itemsPerPage={10}
                showSearch
                searchPlaceholder="Personel adı, sicil no, bölüm, model veya cihaz ID ara..."
                emptyMessage={
                    statusFilter === 'ALL'
                        ? "Sisteme kayıtlı mobil cihaz bulunamadı."
                        : "Seçili filtreye uygun mobil cihaz bulunamadı."
                }
            />

            {/* Pairing Modal */}
            <InfoModal
                isOpen={isPairingModalOpen}
                onClose={() => setIsPairingModalOpen(false)}
                title="Yeni Cihaz Eşleştirme Kodu Üret"
            >
                <div className="space-y-4 min-w-[320px] sm:min-w-[400px]">
                    {!generatedCode ? (
                        <>
                            <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                                Seçtiğiniz personel için <strong>15 dakika geçerli, tek kullanımlık 6 haneli</strong> bir eşleştirme kodu üretilir.
                                {!allowMultipleDevices && (
                                    <span className="block mt-1 text-amber-600 dark:text-amber-400 font-medium">
                                        Not: Tek Cihaz Politikası aktif olduğu için personel bu kodla yeni cihazını eşleştirdiğinde eski cihazı otomatik olarak devre dışı kalır.
                                    </span>
                                )}
                            </p>

                            <div className="space-y-2">
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300">
                                    Personel Ara ve Seç
                                </label>
                                <div className="relative">
                                    <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                    <input
                                        type="text"
                                        value={employeeSearch}
                                        onChange={(e) => setEmployeeSearch(e.target.value)}
                                        placeholder="İsim, T.C. Kimlik veya Sicil No ile filtrele..."
                                        className="w-full pl-9 pr-3 py-2 text-xs border border-gray-300 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none"
                                    />
                                </div>
                                <select
                                    className="w-full p-2.5 text-sm border border-gray-300 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                    value={selectedEmployeeId}
                                    onChange={(e) => setSelectedEmployeeId(e.target.value)}
                                >
                                    <option value="">Personel Seçiniz ({filteredEmployees.length} kayıt)...</option>
                                    {filteredEmployees.map(emp => (
                                        <option key={emp.id} value={emp.id}>
                                            {emp.firstName} {emp.lastName} {emp.turkishIdNo ? `(${emp.turkishIdNo})` : ''} {(emp.sicilNo || emp.employeeNumber) ? `- #${emp.sicilNo || emp.employeeNumber}` : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {error && (
                                <div className="p-2.5 rounded-xl bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 text-xs">
                                    {error}
                                </div>
                            )}

                            <button
                                type="button"
                                onClick={handleGenerateCode}
                                disabled={pairingLoading || !selectedEmployeeId}
                                className="w-full py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center gap-2 transition-colors"
                            >
                                {pairingLoading ? 'Kod Üretiliyor...' : '6 Haneli Eşleştirme Kodu Oluştur'}
                            </button>
                        </>
                    ) : (
                        <div className="text-center py-4">
                            <div className="mb-3">
                                <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                                    Tek Kullanımlık Eşleştirme Kodu
                                </span>
                                <div className="inline-flex items-center gap-2 bg-gray-100 dark:bg-gray-800 px-5 py-3 rounded-2xl border border-dashed border-indigo-400 dark:border-indigo-500">
                                    <span className="text-3xl font-mono font-bold text-indigo-600 dark:text-indigo-400 tracking-[0.25em]">
                                        {generatedCode.code}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={copyPairingCode}
                                        className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-300"
                                        title="Kodu Kopyala"
                                    >
                                        {copiedCode ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>

                            <p className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold mb-5">
                                {generatedCode.expiresInfo || 'Bu kod 15 dakika boyunca geçerlidir.'}
                            </p>

                            <div className="text-xs text-left bg-indigo-50/80 dark:bg-indigo-950/30 text-indigo-900 dark:text-indigo-200 p-3.5 rounded-xl border border-indigo-200/60 dark:border-indigo-800/40">
                                <p className="font-bold mb-1.5">Personel İçin Uygulama Adımları:</p>
                                <ol className="list-decimal pl-4 space-y-1 text-[11px] leading-relaxed">
                                    <li>Personel mobil uygulamayı açar ve kullanıcı bilgileriyle giriş yapar.</li>
                                    <li><strong>"Cihaz Onayı Gerekli"</strong> ekranında bu 6 haneli kodu girer.</li>
                                    <li>Cihaz otomatik olarak onaylanır ve güvenli oturum başlatılır.</li>
                                </ol>
                            </div>

                            <button
                                type="button"
                                onClick={() => { setGeneratedCode(null); setSelectedEmployeeId(''); }}
                                className="mt-5 text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline"
                            >
                                Başka Personel İçin Kod Üret
                            </button>
                        </div>
                    )}
                </div>
            </InfoModal>

            {/* Action Confirm Modal */}
            <InfoModal
                isOpen={isConfirmModalOpen}
                onClose={() => setIsConfirmModalOpen(false)}
                title="Mobil Cihaz İşlem Onayı"
                message={
                    confirmActionData
                        ? `"${confirmActionData.userName || 'Personel'}" kullanıcısına ait (${confirmActionData.model || confirmActionData.deviceId}) cihaz için "${
                            confirmActionData.action === 'Delete'
                                ? 'KAYDI SİLME VE OTURUMU SONLANDIRMA'
                                : confirmActionData.action === 'Block'
                                    ? 'ENGELLEME'
                                    : !allowMultipleDevices
                                        ? 'ONAYLAMA (Varsa personelin diğer cihazları pasife alınacaktır)'
                                        : 'ONAYLAMA'
                        }" işlemini yapmak istediğinize emin misiniz?`
                        : ""
                }
                type="confirm"
                onConfirm={handleActionConfirm}
                confirmText="Evet, Uygula"
                cancelText="İptal"
            />
        </div>
    );
}
